import React, { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { formatDate } from '../utils/formatterDate';
import { useToast } from '../contexts/ToastContext';
import {
  fetchQuotePublicDetail,
  updateQuotePublicItem,
  updateQuotePublic,
  rejectQuotePublicItem,
  sendQuotePublicConfirmation,
  selectQuotePublicCurrentQuote,
  selectQuotePublicQuoteItems,
  selectIsQuotePublicDetailLoading,
  selectQuotePublicDetailError
} from '../store/slices/quotePublicSlice';
import { AppDispatch } from '../store/index';
import { useParams } from 'react-router-dom';

import ConfirmationModal from './Shared/ConfirmationModal';
import ItemViewPublic from './Shared/ItemViewPublic';
import PriceInput from './Shared/PriceInput';
import MasLogo from '../images/MAS.png';

const QuoteDocumentPage: React.FC = () => {
  const { showSuccess, showError, showWarning } = useToast();
  const dispatch = useDispatch<AppDispatch>();
  const { publicId } = useParams<{ publicId: string }>();
  
  // Redux selectors
  const currentQuote = useSelector(selectQuotePublicCurrentQuote);
  const quoteItems = useSelector(selectQuotePublicQuoteItems);
  const isLoading = useSelector(selectIsQuotePublicDetailLoading);
  const error = useSelector(selectQuotePublicDetailError);
  
  const [selectedCurrency, setSelectedCurrency] = useState('');
  const [vatIncluded, setVatIncluded] = useState(false);

  const [supplierNotes, setSupplierNotes] = useState('');
  const [requesterNotes, setRequesterNotes] = useState('');
  const [paymentTerms, setPaymentTerms] = useState('');
  const [validUntil, setValidUntil] = useState<number | ''>('');
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [productToDelete, setProductToDelete] = useState<{id: number, name: string} | null>(null);
  const [showSaveConfirmModal, setShowConfirmModal] = useState(false);
  const [showSaveInfoModal, setShowSaveInfoModal] = useState(false);
  const [showItemModal, setShowItemModal] = useState(false);
  const [selectedItemId, setSelectedItemId] = useState<number | null>(null);
  const [selectedItemData, setSelectedItemData] = useState<any>(null);
  const [showSuccessMessage, setShowSuccessMessage] = useState(false);

  // Products state - will be populated from API
  const [products, setProducts] = useState<any[]>([]);

  // Fetch quote public detail on component mount
  useEffect(() => {
    if (publicId) {
      dispatch(fetchQuotePublicDetail({ publicId }));
    }
  }, [dispatch, publicId]);

  // Allow page scrolling only on this public quote document screen.
  useEffect(() => {
    document.documentElement.classList.add('quote-document-scroll-enabled');

    return () => {
      document.documentElement.classList.remove('quote-document-scroll-enabled');
    };
  }, []);

  const parseApiDecimal = (raw: unknown): number => {
    if (raw === null || raw === undefined || raw === '') return 0;
    if (typeof raw === 'number') return Number.isFinite(raw) ? raw : 0;
    const n = parseFloat(String(raw).trim().replace(',', '.'));
    return Number.isFinite(n) ? n : 0;
  };

  // Update products when quoteItems change
  useEffect(() => {
    if (quoteItems && quoteItems.length > 0) {
      const mappedProducts = quoteItems.map((item, index) => {
        const images = item.item?.itemImages?.map(img => img.imageUrl || '').filter(url => url) || [];
        console.log('Product images for', item.itemName, ':', images); // Debug log
        const unitPrice = parseApiDecimal(item.offeredPrice);
        const qty = parseApiDecimal(item.offeredQuantity);
        return {
          id: item.id || index + 1,
          code: item.itemCode || '',
          name: item.itemName || '',
          description: item.item?.description || '',
          images: images,
          requestedQuantity: item.requestedQuantity || 0,
          quantity: item.offeredQuantity || 0,
          unit: item.unitName || '',
          unitPrice,
          vatRate: item.vatRate || 0,
          deliveryDays: item.offeredDeliveryDays || 0,
          total: qty * unitPrice
        };
      });
      setProducts(mappedProducts);
    }
  }, [quoteItems]);

  // Update requester notes when currentQuote changes
  useEffect(() => {
    if (currentQuote?.noteToSupplier) {
      setRequesterNotes(currentQuote.noteToSupplier);
    }
    if (currentQuote?.noteFromSupplier) {
      setSupplierNotes(currentQuote.noteFromSupplier);
    }
    
    // Para birimi ayarı:
    // Eğer backend'den para birimi geliyorsa, bunu sadece belirli durumlarda set et.
    // Kullanıcı varsayılan olarak boş gelmesini istediği için, eğer fiyat girilmemişse ve para birimi varsayılan (TRY) ise boş bırak.
    if (currentQuote?.currency) {
      const isDefaultCurrency = currentQuote.currency === 'TRY';
      const hasPricedItems = quoteItems && quoteItems.length > 0 && quoteItems.some(item => (item.offeredPrice || 0) > 0);
      
      // Eğer para birimi TRY değilse (yani değiştirilmişse) VEYA fiyat girilmiş ürün varsa set et.
      // Aksi takdirde (TRY ve fiyat yoksa) boş bırak ki kullanıcı seçmek zorunda kalsın.
      if (!isDefaultCurrency || hasPricedItems) {
        setSelectedCurrency(currentQuote.currency);
      } else {
        setSelectedCurrency('');
      }
    }
    
    if (currentQuote?.vatIncluded !== undefined) {
      setVatIncluded(currentQuote.vatIncluded);
    }
    if (currentQuote?.paymentTerm) {
      setPaymentTerms(currentQuote.paymentTerm);
    }
    if (currentQuote?.validUntil !== undefined && currentQuote?.validUntil !== null) {
      setValidUntil(currentQuote.validUntil);
    } else {
      setValidUntil('');
    }
  }, [currentQuote, quoteItems]);


  const updateProduct = (id: number, field: string, value: number | string) => {
    setProducts(prev => prev.map(product => {
      if (product.id === id) {
        const updated = { ...product, [field]: value };
        if (field === 'quantity' || field === 'unitPrice') {
          const qty = typeof updated.quantity === 'string' ? (updated.quantity === '' ? 0 : parseFloat(updated.quantity) || 0) : updated.quantity;
          const price = typeof updated.unitPrice === 'string' ? (updated.unitPrice === '' ? 0 : parseFloat(updated.unitPrice) || 0) : updated.unitPrice;
          updated.total = Math.round((qty * price) * 10000) / 10000;
        }
        return updated;
      }
      return product;
    }));
  };


  // Basit toplam hesaplama
  const getTotal = (unitPrice: number, quantity: number | string) => {
    const qty = typeof quantity === 'string' ? (quantity === '' ? 0 : parseFloat(quantity) || 0) : quantity;
    return unitPrice * qty;
  };

  const deleteProduct = (id: number) => {
    const product = products.find(product => product.id === id);
    if (product) {
      setProductToDelete({ id: product.id, name: product.name });
      setShowDeleteModal(true);
    }
  };

  const confirmDelete = async () => {
    if (productToDelete && publicId) {
      try {
        const result = await dispatch(rejectQuotePublicItem({ 
          publicId, 
          quoteItemId: productToDelete.id 
        }));
        
        if (result.type.endsWith('/fulfilled')) {
          // API çağrısı başarılıysa local state'den de sil
          setProducts(prev => prev.filter(product => product.id !== productToDelete.id));
          showSuccess('Ürün başarıyla silindi!');
        } else {
          showError('Ürün silinirken hata oluştu!');
        }
      } catch (error) {
        showError('Ürün silinirken bir hata oluştu!');
      } finally {
        setShowDeleteModal(false);
        setProductToDelete(null);
      }
    }
  };

  const getSubtotal = () => {
    if (vatIncluded) {
      // KDV dahil ise, KDV'siz tutarı hesapla
      return products.reduce((sum, product) => {
        const productTotal = getTotal(product.unitPrice, product.quantity);
        const vatAmount = (productTotal * product.vatRate) / (100 + product.vatRate);
        return sum + (productTotal - vatAmount);
      }, 0);
    } else {
      // KDV hariç ise, direkt toplam
      return products.reduce((sum, product) => sum + getTotal(product.unitPrice, product.quantity), 0);
    }
  };

  const getVatBreakdown = () => {
    const vatGroups: { [key: number]: number } = {};
    
    products.forEach(product => {
      const productTotal = getTotal(product.unitPrice, product.quantity);
      let vatAmount = 0;
      
      if (vatIncluded) {
        // KDV dahil ise, KDV tutarını çıkar
        vatAmount = (productTotal * product.vatRate) / (100 + product.vatRate);
      } else {
        // KDV hariç ise, KDV tutarını ekle
        vatAmount = (productTotal * product.vatRate) / 100;
      }
      
      const existingAmount = vatGroups[product.vatRate];
      vatGroups[product.vatRate] = (existingAmount ?? 0) + vatAmount;
    });
    
    return vatGroups;
  };

  const getVatTotal = () => {
    const vatBreakdown = getVatBreakdown();
    return Object.values(vatBreakdown).reduce((sum, amount) => sum + amount, 0);
  };

  const getGrandTotal = () => {
    if (vatIncluded) {
      // KDV dahil ise, ürün fiyatları zaten KDV dahil
      return products.reduce((sum, product) => sum + getTotal(product.unitPrice, product.quantity), 0);
    } else {
      // KDV hariç ise, KDV'yi ekle
      return getSubtotal() + getVatTotal();
    }
  };

  // Son tarih hesaplama - emailSentAt + timeoutDays
  const getDeadlineDate = () => {
    if (currentQuote?.emailSentAt && currentQuote?.timeoutDays) {
      const emailDate = new Date(currentQuote.emailSentAt);
      const deadlineDate = new Date(emailDate);
      deadlineDate.setDate(deadlineDate.getDate() + currentQuote.timeoutDays);
      return deadlineDate;
    }
    return null;
  };



  const handleSave = () => {
    setShowConfirmModal(true);
  };

  const handleSaveOnly = () => {
    setShowSaveInfoModal(true);
  };

  const executeSaveOnly = async () => {
    try {
      // Valid until validation
      if (validUntil === '' || validUntil === null || validUntil === undefined) {
        showError('Lütfen teklif geçerlilik süresini giriniz!');
        return;
      }

      // Ürün alanları validasyonu
      if (products.length > 0) {
        for (const product of products) {
          if (!product.quantity || product.quantity <= 0) {
            showError(`"${product.name}" için miktar girilmelidir!`);
            return;
          }
          if (!product.unitPrice || product.unitPrice <= 0) {
            showError(`"${product.name}" için birim fiyat girilmelidir!`);
            return;
          }
          if (product.deliveryDays === undefined || product.deliveryDays === null || product.deliveryDays <= 0) {
            showError(`"${product.name}" için teslimat süresi girilmelidir!`);
            return;
          }
        }
      }
      
      // İlk aşama: Her ürün için updateQuotePublicItem çağrısı
      if (publicId && products.length > 0) {
        for (const product of products) {
          const updateData = {
            offeredQuantity: product.quantity,
            offeredPrice: product.unitPrice,
            offeredDeliveryDays: product.deliveryDays,
            vatRate: product.vatRate
          };
          
          const result = await dispatch(updateQuotePublicItem({ 
            publicId, 
            quoteItemId: product.id,
            data: updateData 
          }));
          
          // Success kontrolü - eğer başarısızsa işlemi durdur
          if (result.type.endsWith('/rejected')) {
            showError('Ürün güncellenirken hata oluştu!');
            return;
          }
        }
      }
      
      // İkinci aşama: Tüm item güncellemeleri başarılıysa updateQuotePublic çağrısı
      if (publicId) {
        const quoteUpdateData = {
          noteFromSupplier: supplierNotes,
          paymentTerm: paymentTerms,
          currency: selectedCurrency,
          vatIncluded,
          validUntil: validUntil,
          quoteStatus: "Editing"
        };
        
        // updateQuotePublic çağrısı (sadece bir kez)
        await dispatch(updateQuotePublic({ 
          publicId, 
          data: quoteUpdateData 
        }));
      }
      
      // Üçüncü aşama: Güncellenmiş teklif detaylarını yeniden getir
      if (publicId) {
        const refreshResult = await dispatch(fetchQuotePublicDetail({ publicId }));
        
        // 3. endpoint başarılı olduysa başarı mesajı
        if (refreshResult.type.endsWith('/fulfilled')) {
          showSuccess('Teklif belgesi başarıyla kaydedildi ve güncellendi!');
        }
      } else {
        showSuccess('Teklif belgesi kaydedildi!');
      }
    } catch (error) {
      showError('Teklif kaydedilirken bir hata oluştu!');
    }
  };

  const handleConfirmSave = async () => {
    try {
      // Valid until validation
      if (validUntil === '' || validUntil === null || validUntil === undefined) {
        showError('Lütfen teklif geçerlilik süresini giriniz!');
        return;
      }

      // Ürün alanları validasyonu
      if (products.length > 0) {
        for (const product of products) {
          if (!product.quantity || product.quantity <= 0) {
            showError(`"${product.name}" için miktar girilmelidir!`);
            return;
          }
          if (!product.unitPrice || product.unitPrice <= 0) {
            showError(`"${product.name}" için birim fiyat girilmelidir!`);
            return;
          }
          if (product.deliveryDays === undefined || product.deliveryDays === null || product.deliveryDays <= 0) {
            showError(`"${product.name}" için teslimat süresi girilmelidir!`);
            return;
          }
        }
      }
      
      setShowConfirmModal(false);
      
      // İlk aşama: Her ürün için updateQuotePublicItem çağrısı
      if (publicId && products.length > 0) {
        for (const product of products) {
          const updateData = {
            offeredQuantity: product.quantity,
            offeredPrice: product.unitPrice,
            offeredDeliveryDays: product.deliveryDays,
            vatRate: product.vatRate
          };
          
          const result = await dispatch(updateQuotePublicItem({ 
            publicId, 
            quoteItemId: product.id,
            data: updateData 
          }));
          
          // Success kontrolü - eğer başarısızsa işlemi durdur
          if (result.type.endsWith('/rejected')) {
            showError('Ürün güncellenirken hata oluştu!');
            return;
          }
        }
      }
      
      // İkinci aşama: Tüm item güncellemeleri başarılıysa updateQuotePublic çağrısı
      if (publicId) {
        const quoteUpdateData = {
          noteFromSupplier: supplierNotes,
          paymentTerm: paymentTerms,
          currency: selectedCurrency,
          vatIncluded,
          validUntil: validUntil,
          quoteStatus: "Responded"
        };
        
        // updateQuotePublic çağrısı (sadece bir kez)
        const updateResult = await dispatch(updateQuotePublic({ 
          publicId, 
          data: quoteUpdateData 
        }));
        
        // Başarı kontrolü
        if (updateResult.type.endsWith('/rejected')) {
          showError('Teklif güncellenirken hata oluştu!');
          return;
        }
        
        // Üçüncü aşama: Tedarikçiye onay emaili gönder (kritik değil)
        if (publicId) {
          try {
            await dispatch(sendQuotePublicConfirmation(publicId)).unwrap();
          } catch {
            showWarning('Onay emaili gönderilemedi, ancak teklifiniz başarıyla kaydedildi.');
          }
        }

        // Başarılıysa success mesajını göster
        setShowSuccessMessage(true);
      } else {
        setShowSuccessMessage(true);
      }
    } catch (error) {
      showError('Teklif kaydedilirken bir hata oluştu!');
    }
  };

  // Show success message after successful save and approve
  if (showSuccessMessage) {
    return (
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="flex justify-center items-center min-h-[80vh]">
          <div className="text-center bg-white dark:bg-gray-800 rounded-lg shadow-lg p-12 max-w-2xl mx-auto">
            <div className="mb-8">
              <div className="mx-auto flex items-center justify-center h-24 w-24 rounded-full bg-green-100 dark:bg-green-900/20 mb-6">
                <i className="fas fa-check-circle text-green-600 dark:text-green-400 text-4xl"></i>
              </div>
              <h1 className="text-4xl font-bold text-gray-900 dark:text-white mb-4">
                İşlem Başarılı!
              </h1>
              <p className="text-xl text-gray-600 dark:text-gray-400 leading-relaxed">
                Teklifiniz Başarıyla Gönderilmiştir
              </p>
            </div>
          </div>
        </div>
      </main>
    );
  }

  // Show loading state
  if (isLoading) {
    return (
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="flex justify-center items-center h-64">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto mb-4"></div>
            <p className="text-gray-600 dark:text-gray-400">Teklif belgesi yükleniyor...</p>
          </div>
        </div>
      </main>
    );
  }

  // Show error state if there's an error
  if (error) {
    return (
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="flex justify-center items-center min-h-[80vh]">
          <div className="text-center bg-white dark:bg-gray-800 rounded-lg shadow-lg p-12 max-w-2xl mx-auto">
            <div className="mb-8">
              <div className="mx-auto flex items-center justify-center h-24 w-24 rounded-full bg-red-100 dark:bg-red-900/20 mb-6">
                <i className="fas fa-exclamation-triangle text-red-600 dark:text-red-400 text-4xl"></i>
              </div>
              <h1 className="text-4xl font-bold text-gray-900 dark:text-white mb-4">
                Link Geçersiz!
              </h1>
              <p className="text-xl text-gray-600 dark:text-gray-400 leading-relaxed mb-6">
                Bu teklif linki geçersiz veya süresi dolmuş olabilir. 
                <br />
                Lütfen doğru link ile tekrar deneyiniz.
              </p>
              <button 
                onClick={() => {
                  if (publicId) {
                    dispatch(fetchQuotePublicDetail({ publicId }));
                  }
                }}
                className="bg-primary text-white px-6 py-3 rounded-lg hover:bg-blue-700 transition-colors text-lg font-medium"
              >
                <i className="fas fa-redo mr-2"></i>Tekrar Dene
              </button>
            </div>
          </div>
        </div>
      </main>
    );
  }


  return (
      
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">


        {/* Document Container */}
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-lg overflow-hidden transition-colors duration-300">
          
          {/* Document Header */}
          <div className="bg-gradient-to-r from-primary to-secondary text-white p-6">
            <div className="flex justify-between items-start">
              <div className="flex items-center space-x-4">
                <img 
                  className="w-48 h-48 object-contain -mt-12 -mb-12" 
                  src={MasLogo} 
                  alt="MAS Logo" 
                />
                <div>
                  <h3 className="text-2xl font-bold mb-2">SATIN ALMA TEKLİF BELGESİ</h3>
                  <p className="text-blue-100">Belge No: {currentQuote?.id ? `SAT-${currentQuote.id}` : 'SAT-2024-001'}</p>
                </div>
              </div>
              <div className="text-right">
                <div className="bg-white/20 backdrop-blur-sm rounded-lg p-4">
                  <div className="text-sm text-blue-100 mb-1">Son Cevaplanma Tarihi</div>
                  <div className="text-lg font-bold text-white">
                    {getDeadlineDate() ? formatDate(getDeadlineDate()!) : '-'}
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Document Body */}
          <div className="p-6">
            
            {/* Company Information */}
            <div className="grid grid-cols-2 gap-8 mb-8">
              <div className="space-y-3">
                <h4 className="font-semibold text-gray-900 dark:text-white text-lg border-b border-gray-200 dark:border-gray-600 pb-2 transition-colors">Talep Eden Şirket</h4>
                <div className="space-y-1 text-gray-700 dark:text-gray-300 transition-colors">
                  <p>MAS Masaüstü ve Ofis Gereçleri San. ve Tic. A.Ş</p>
                  <p>İkitelli OSB, Eski Turgut Özal Cd. No:28, 34306 Başakşehir/İstanbul</p>
                  <p>İstanbul</p>
                  <p>(0212) 549 07 07</p>
                  <p>mas@masburo.com</p>
                </div>
              </div>
              <div className="space-y-3">
                <h4 className="font-semibold text-gray-900 dark:text-white text-lg border-b border-gray-200 dark:border-gray-600 pb-2 transition-colors">Tedarikçi Bilgileri</h4>
                <div className="space-y-1 text-gray-700 dark:text-gray-300 transition-colors">
                  <p>{currentQuote?.supplier?.companyName || currentQuote?.supplierName || '-'}</p>
                  <p>{currentQuote?.supplier?.contactPerson || '-'}</p>
                  <p>{currentQuote?.supplier?.address || '-'}</p>
                  <p>{currentQuote?.supplier?.city || '-'}</p>
                  <p>{currentQuote?.supplier?.phone || '-'}</p>
                  <p>{currentQuote?.supplier?.email || currentQuote?.supplierEmail || '-'}</p>
                </div>
              </div>
            </div>

            {/* Requester Notes Section */}
            {requesterNotes && requesterNotes.trim() && (
              <div className="mb-8">
                <div className="bg-yellow-200 dark:bg-yellow-600 rounded-lg p-4 border-2 border-yellow-400 dark:border-yellow-500 shadow-lg transition-colors">
                  <div className="text-gray-800 dark:text-gray-200 text-sm leading-relaxed transition-colors">
                    {requesterNotes}
                  </div>
                </div>
              </div>
            )}

            {/* Products Section */}
            <div className="mb-8">
              <div className="flex justify-between items-center border-b border-gray-200 dark:border-gray-600 pb-2 mb-4 transition-colors">
                <h4 className="font-semibold text-gray-900 dark:text-white text-lg transition-colors">Talep Edilen Ürünler</h4>
                <div className="flex items-center space-x-4">
                  <div className="flex items-center space-x-2">
                    <label className="text-sm font-medium text-gray-700 dark:text-gray-300 transition-colors">Para Birimi:</label>
                    <select 
                      value={selectedCurrency}
                      onChange={(e) => setSelectedCurrency(e.target.value)}
                      className={`px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-white transition-colors ${!selectedCurrency ? 'border-red-500 ring-2 ring-red-200' : ''}`}
                    >
                      <option value="" disabled>Seçiniz</option>
                      <option value="TRY">TRY</option>
                      <option value="USD">USD</option>
                      <option value="EUR">EUR</option>
                    </select>
                  </div>
                  <div className="flex items-center space-x-2">
                    <label className="text-sm font-medium text-gray-700 dark:text-gray-300 transition-colors">KDV:</label>
                    <select 
                      value={vatIncluded ? 'included' : 'excluded'}
                      onChange={(e) => setVatIncluded(e.target.value === 'included')}
                      className="px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-white transition-colors"
                    >
                      <option value="excluded">KDV Hariç</option>
                      <option value="included">KDV Dahil</option>
                    </select>
                  </div>
                </div>
              </div>
              <div>
                <table className="w-full border border-gray-300 dark:border-gray-600" style={{ tableLayout: 'fixed' }}>
                  <thead className="bg-gray-50 dark:bg-gray-700">
                    <tr>
                      <th className="border border-gray-300 dark:border-gray-600 px-2 py-3 text-left text-sm font-medium text-gray-700 dark:text-gray-300 transition-colors" style={{ width: '60px' }}>Ürün Kodu</th>
                      <th className="border border-gray-300 dark:border-gray-600 px-2 py-3 text-left text-sm font-medium text-gray-700 dark:text-gray-300 transition-colors" style={{ width: '80px' }}>Ürün Adı</th>
                      <th className="border border-gray-300 dark:border-gray-600 px-2 py-3 text-center text-sm font-medium text-gray-700 dark:text-gray-300 transition-colors" style={{ width: '60px' }}>Birim</th>
                      <th className="border border-gray-300 dark:border-gray-600 px-2 py-3 text-right text-sm font-medium text-gray-700 dark:text-gray-300 transition-colors" style={{ width: '60px' }}>Miktar</th>
                      <th
                        className="border border-gray-300 dark:border-gray-600 px-2 py-3 text-right text-sm font-medium text-gray-700 dark:text-gray-300 transition-colors whitespace-nowrap"
                        style={{ width: '128px', minWidth: '128px' }}
                      >
                        Birim Fiyat
                      </th>
                      <th className="border border-gray-300 dark:border-gray-600 px-2 py-3 text-center text-sm font-medium text-gray-700 dark:text-gray-300 transition-colors" style={{ width: '50px' }}>KDV (%)</th>
                      <th className="border border-gray-300 dark:border-gray-600 px-2 py-3 text-center text-sm font-medium text-gray-700 dark:text-gray-300 transition-colors" style={{ width: '60px' }}>Teslimat (Gün)</th>
                      <th
                        className="border border-gray-300 dark:border-gray-600 px-2 py-3 text-center text-sm font-medium text-gray-700 dark:text-gray-300 transition-colors whitespace-nowrap"
                        style={{ width: '112px', minWidth: '112px' }}
                      >
                        Tutar
                      </th>
                      <th className="border border-gray-300 dark:border-gray-600 px-0 py-1 text-center text-sm font-medium text-gray-700 dark:text-gray-300 transition-colors" style={{ width: '15px' }}></th>
                    </tr>
                  </thead>
                  <tbody>
                    {products.map((product) => (
                      <tr key={product.id} className="hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors">
                        <td className="border border-gray-300 dark:border-gray-600 px-2 py-3 text-sm text-gray-900 dark:text-white transition-colors">{product.code}</td>
                        <td className="border border-gray-300 dark:border-gray-600 px-2 py-3 text-sm text-gray-900 dark:text-white transition-colors">
                          <button 
                            onClick={() => {
                              setSelectedItemId(product.id);
                              setSelectedItemData({
                                id: product.id.toString(),
                                title: product.name,
                                description: product.description,
                                images: product.images
                              });
                              setShowItemModal(true);
                            }}
                            className="text-blue-600 hover:text-blue-800 underline cursor-pointer"
                          >
                            {product.name}
                          </button>
                        </td>
                        <td className="border border-gray-300 dark:border-gray-600 px-2 py-3 text-sm text-center text-gray-900 dark:text-white transition-colors relative">
                          <span>{product.unit}</span>
                        </td>
                        <td className="border border-gray-300 dark:border-gray-600 px-0 py-0 text-sm text-right bg-blue-50 dark:bg-blue-900/10">
                          <input 
                            type="text" 
                            disabled={!selectedCurrency}
                            className={`w-full h-full px-3 py-3 border-0 text-right focus:outline-none focus:ring-2 focus:ring-inset focus:ring-primary bg-transparent hover:bg-blue-100 dark:hover:bg-blue-900/20 text-gray-900 dark:text-white transition-colors cursor-pointer focus:cursor-text font-bold ${!selectedCurrency ? 'opacity-50 cursor-not-allowed' : ''}`} 
                            value={product.quantity === '' ? '' : Number(product.quantity).toLocaleString('tr-TR')}
                            onChange={(e) => {
                              const value = e.target.value.replace(/\./g, ''); // Remove thousand separators
                              if (value === '') {
                                updateProduct(product.id, 'quantity', '');
                              } else {
                                const numValue = parseInt(value);
                                updateProduct(product.id, 'quantity', isNaN(numValue) ? 0 : numValue);
                              }
                            }}
                            onFocus={(e) => {
                              e.target.select();
                              // Show raw number when focused for easier editing
                              const rawValue = product.quantity === '' ? '' : product.quantity.toString();
                              e.target.value = rawValue;
                            }}
                            onBlur={(e) => {
                              // Format the number when losing focus
                              if (e.target.value !== '') {
                                const numValue = parseInt(e.target.value.replace(/\./g, ''));
                                if (!isNaN(numValue)) {
                                  e.target.value = numValue.toLocaleString('tr-TR');
                                }
                              }
                            }}
                            title="Düzenlemek için tıklayın"
                          />
                        </td>
                        <td className="border border-gray-300 dark:border-gray-600 px-0 py-0 text-sm text-right bg-blue-50 dark:bg-blue-900/10 whitespace-nowrap align-middle">
                          <PriceInput
                            value={product.unitPrice}
                            disabled={!selectedCurrency}
                            onChange={(value) => updateProduct(product.id, 'unitPrice', value)}
                            className={`w-full min-w-[7rem] h-full px-2 py-3 border-0 text-right text-sm focus:outline-none focus:ring-2 focus:ring-inset focus:ring-primary bg-transparent hover:bg-blue-100 dark:hover:bg-blue-900/20 text-gray-900 dark:text-white transition-colors cursor-pointer focus:cursor-text font-bold ${!selectedCurrency ? 'opacity-50 cursor-not-allowed' : ''}`}
                            onFocus={(e) => e.target.select()}
                            title="Düzenlemek için tıklayın"
                            allowNegative={false}
                            decimalScale={4}
                            fixedDecimalScale={true}
                          />
                        </td>
                        <td className="border border-gray-300 dark:border-gray-600 px-0 py-0 text-sm text-center bg-blue-50 dark:bg-blue-900/10">
                          <input 
                            type="number" 
                            disabled={!selectedCurrency}
                            className={`w-full h-full px-2 py-3 border-0 text-center focus:outline-none focus:ring-2 focus:ring-inset focus:ring-primary bg-transparent hover:bg-blue-100 dark:hover:bg-blue-900/20 text-gray-900 dark:text-white transition-colors cursor-pointer focus:cursor-text font-bold [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none [-moz-appearance:textfield] ${!selectedCurrency ? 'opacity-50 cursor-not-allowed' : ''}`} 
                            value={product.vatRate}
                            onChange={(e) => {
                              const value = e.target.value;
                              if (value === '') {
                                updateProduct(product.id, 'vatRate', '');
                              } else {
                                const numValue = parseInt(value);
                                updateProduct(product.id, 'vatRate', isNaN(numValue) ? 0 : numValue);
                              }
                            }}
                            min="0"
                            max="100"
                            onFocus={(e) => e.target.select()}
                            title="Düzenlemek için tıklayın"
                          />
                        </td>
                        <td className="border border-gray-300 dark:border-gray-600 px-0 py-0 text-sm text-center bg-blue-50 dark:bg-blue-900/10">
                          <input 
                            type="number" 
                            disabled={!selectedCurrency}
                            className={`w-full h-full px-3 py-3 border-0 text-center focus:outline-none focus:ring-2 focus:ring-inset focus:ring-primary bg-transparent hover:bg-blue-100 dark:hover:bg-blue-900/20 text-gray-900 dark:text-white transition-colors cursor-pointer focus:cursor-text font-bold [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none [-moz-appearance:textfield] ${!selectedCurrency ? 'opacity-50 cursor-not-allowed' : ''}`} 
                            value={product.deliveryDays}
                            onChange={(e) => {
                              const value = e.target.value;
                              if (value === '') {
                                updateProduct(product.id, 'deliveryDays', '');
                              } else {
                                const numValue = parseInt(value);
                                updateProduct(product.id, 'deliveryDays', isNaN(numValue) ? 0 : numValue);
                              }
                            }}
                            onFocus={(e) => e.target.select()}
                            title="Düzenlemek için tıklayın"
                          />
                        </td>
                        <td className="border border-gray-300 dark:border-gray-600 px-2 py-3 text-sm text-right font-medium text-gray-900 dark:text-white transition-colors tabular-nums whitespace-nowrap">
                          {getTotal(product.unitPrice, product.quantity).toLocaleString('tr-TR', { minimumFractionDigits: 4, maximumFractionDigits: 4 })} {selectedCurrency}
                        </td>
                        <td className="border border-gray-300 dark:border-gray-600 px-0 py-1 text-center" style={{ width: '15px' }}>
                          <button 
                            onClick={() => deleteProduct(product.id)}
                            className="text-red-600 hover:text-red-800 transition-colors"
                            title="Ürünü Sil"
                          >
                            <i className="fas fa-trash text-sm"></i>
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                  <tfoot>
                    {/* Ara Toplam */}
                    <tr className="bg-gray-50 dark:bg-gray-700 border-t border-gray-300 dark:border-gray-600">
                      <td colSpan={7} className="px-2 py-2 text-right font-semibold text-sm text-gray-700 dark:text-gray-300 transition-colors">
                        <i className="fas fa-calculator mr-1"></i>ARA TOPLAM:
                      </td>
                      <td className="px-2 py-2 text-right font-bold text-sm text-gray-700 dark:text-gray-300 transition-colors">
                        {getSubtotal().toLocaleString('tr-TR', { minimumFractionDigits: 4, maximumFractionDigits: 4 })} {selectedCurrency}
                      </td>
                      <td className="px-0 py-1 text-center" style={{ width: '15px' }}></td>
                    </tr>
                    
                    {/* KDV Detayları - Her KDV oranı için ayrı satır */}
                    {Object.entries(getVatBreakdown()).map(([vatRate, amount]) => (
                      <tr key={vatRate} className="bg-gray-50 dark:bg-gray-700 border-t border-gray-300 dark:border-gray-600">
                        <td colSpan={7} className="px-2 py-2 text-right font-semibold text-sm text-gray-700 dark:text-gray-300 transition-colors">
                          <i className="fas mr-1"></i>KDV (%{vatRate}) TUTARI:
                        </td>
                        <td className="px-2 py-2 text-right font-bold text-sm text-gray-700 dark:text-gray-300 transition-colors">
                          {amount.toLocaleString('tr-TR', { minimumFractionDigits: 4, maximumFractionDigits: 4 })} {selectedCurrency}
                        </td>
                        <td className="px-0 py-1 text-center" style={{ width: '15px' }}></td>
                      </tr>
                    ))}
                    
                    {/* Genel Toplam */}
                    <tr className="bg-gray-100 dark:bg-gray-600 border-t-2 border-gray-400 dark:border-gray-500">
                      <td colSpan={7} className="px-2 py-2 text-right font-bold text-base text-gray-800 dark:text-gray-200 transition-colors">
                        <i className="fas fa-coins mr-1"></i>GENEL TOPLAM:
                      </td>
                      <td className="px-2 py-2 text-right font-bold text-base text-gray-800 dark:text-gray-200 transition-colors">
                        {getGrandTotal().toLocaleString('tr-TR', { minimumFractionDigits: 4, maximumFractionDigits: 4 })} {selectedCurrency}
                      </td>
                      <td className="px-0 py-1 text-center" style={{ width: '15px' }}></td>
                    </tr>
                  </tfoot>
                </table>
              </div>
            </div>

            {/* Payment Terms and Validity Days Section */}
            <div className={`mb-8 grid gap-6 ${currentQuote?.supplier?.showPaymentTerm === true ? 'grid-cols-[2fr_1fr]' : 'grid-cols-1'}`}>
              {/* Payment Terms Section - Only show if showPaymentTerm === true */}
              {currentQuote?.supplier?.showPaymentTerm === true && (
                <div className="flex flex-col">
                  <h4 className="font-semibold text-gray-900 dark:text-white text-lg border-b border-gray-200 dark:border-gray-600 pb-2 mb-4 transition-colors">Ödeme Koşulları</h4>
                  <div className="bg-gray-50 dark:bg-gray-700 rounded-lg p-4 border border-gray-300 dark:border-gray-600 transition-colors flex-1 flex flex-col">
                    <textarea 
                      className="w-full h-24 px-4 py-3 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent resize-none bg-white dark:bg-gray-800 text-gray-900 dark:text-white placeholder-gray-500 dark:placeholder-gray-400 transition-colors flex-1"
                      placeholder="Ödeme koşullarınızı buraya girebilirsiniz (örn: 30 gün vadeli, peşin ödeme, %50 peşin %50 teslimde vb.)..."
                      value={paymentTerms}
                      onChange={(e) => setPaymentTerms(e.target.value)}
                    />
                  </div>
                </div>
              )}

              {/* Validity Days Section */}
              <div className="flex flex-col">
                <h4 className="font-semibold text-gray-900 dark:text-white text-lg border-b border-gray-200 dark:border-gray-600 pb-2 mb-4 transition-colors">Teklif Geçerlilik Süresi <span className="text-red-500">*</span></h4>
                <div className="bg-gray-50 dark:bg-gray-700 rounded-lg p-4 border border-gray-300 dark:border-gray-600 transition-colors flex-1 flex flex-col justify-center">
                  <div className="flex items-center space-x-4">
                    <label className="text-sm font-medium text-gray-700 dark:text-gray-300 transition-colors">
                      Teklif geçerlilik süresi: <span className="text-red-500">*</span>
                    </label>
                    <div className="flex items-center space-x-2">
                      <input
                        type="number"
                        min="1"
                        max="365"
                        value={validUntil === '' ? '' : validUntil}
                        onChange={(e) => {
                          const value = e.target.value;
                          if (value === '') {
                            setValidUntil('');
                          } else {
                            const numValue = parseInt(value);
                            if (!isNaN(numValue) && numValue > 0) {
                              setValidUntil(numValue);
                            }
                          }
                        }}
                        className="w-20 px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent bg-white dark:bg-gray-800 text-gray-900 dark:text-white transition-colors [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none [-moz-appearance:textfield]"
                      />
                      <span className="text-sm text-gray-600 dark:text-gray-400 transition-colors">gün</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Notes Section */}
            <div className="mb-8">
              <h4 className="font-semibold text-gray-900 dark:text-white text-lg border-b border-gray-200 dark:border-gray-600 pb-2 mb-4 transition-colors">Tedarikçi Notları</h4>
              <div className="bg-gray-50 dark:bg-gray-700 rounded-lg p-4 transition-colors">
                <textarea 
                  className="w-full h-32 px-4 py-3 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent resize-none bg-white dark:bg-gray-800 text-gray-900 dark:text-white placeholder-gray-500 dark:placeholder-gray-400 transition-colors"
                  placeholder="Tedarikçi notlarınızı buraya girebilirsiniz..."
                  value={supplierNotes}
                  onChange={(e) => setSupplierNotes(e.target.value)}
                />
              </div>
            </div>

            {/* Terms Section */}
            <div className="space-y-8 mb-8">
              <div className="grid grid-cols-2 gap-8">
                <div className="space-y-4">
                  <h4 className="font-semibold text-gray-900 dark:text-white text-lg border-b border-gray-200 dark:border-gray-600 pb-2 transition-colors">Teslimat Bilgileri</h4>
                  <div className="space-y-3">
                    <div className="flex items-start space-x-3">
                      <label className="text-sm font-medium text-gray-700 dark:text-gray-300 w-32 mt-2 transition-colors">Teslim Yeri:</label>
                      <div className="flex-1">
                        <div className="text-gray-700 dark:text-gray-300 text-sm mb-2 transition-colors">
                          İkitelli OSB, Eski Turgut Özal Cd. No:28, 34306 Başakşehir/İstanbul
                        </div>
                        <div className="text-gray-500 dark:text-gray-400 text-xs italic transition-colors">
                          Belirtilen mallar belirtilen sürede tedarikçi tarafından bu adrese teslim edilecektir.
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Bottom Action Buttons */}
        <div className="mt-8 space-y-6 no-print">
          {/* Bilgilendirme Mesajları */}
          <div className="flex justify-between gap-4">
            {/* Sol Bilgilendirme */}
            <div className="flex-1 bg-blue-50 border border-blue-200 rounded-md p-3 hidden">
              <div className="flex items-start space-x-2">
                <i className="fas fa-info-circle text-blue-500 mt-0.5 flex-shrink-0"></i>
                <p className="text-xs text-blue-700 leading-relaxed">
                  <strong>Kaydet ve Sonra Devam Et:</strong> Teklifinizi taslak olarak kaydeder. Daha sonra düzenlemeye devam edebilir ve tekrar kaydedebilirsiniz.
                </p>
              </div>
            </div>
            
            {/* Sağ Bilgilendirme */}
            <div className="flex-1 bg-blue-50 border border-blue-200 rounded-md p-3">
              <div className="flex items-start space-x-2">
                <i className="fas fa-info-circle text-blue-500 mt-0.5 flex-shrink-0"></i>
                <p className="text-xs text-blue-700 leading-relaxed">
                  <strong>Kaydet & Onayla:</strong> Teklifinizi kaydeder ve onaylar. Onayladıktan sonra bu teklifi değiştiremezsiniz.
                </p>
              </div>
            </div>
          </div>

          {/* Butonlar */}
          <div className="flex justify-center gap-4">
            <button 
              onClick={handleSaveOnly}
              className="bg-gray-600 text-white px-8 py-3 rounded-lg hover:bg-gray-700 transition-colors text-lg font-medium hidden"
            >
              <i className="fas fa-save mr-2"></i>Kaydet ve Sonra Devam Et
            </button>
            <button 
              onClick={handleSave}
              className="bg-primary text-white px-8 py-3 rounded-lg hover:bg-blue-700 transition-colors text-lg font-medium"
            >
              <i className="fas fa-check-circle mr-2"></i>Kaydet & Onayla
            </button>
          </div>
        </div>

        {/* Delete Confirmation Modal */}
        <ConfirmationModal
          isOpen={showDeleteModal}
          onClose={() => {
            setShowDeleteModal(false);
            setProductToDelete(null);
          }}
          onConfirm={confirmDelete}
          title="Ürün Silme Onayı"
          message={`${productToDelete?.name} ürününü silmek istediğinizden emin misiniz?`}
          confirmText="Sil"
          type="danger"
          icon="fas fa-trash"
        />

        {/* Save Confirmation Modal */}
        <ConfirmationModal
          isOpen={showSaveConfirmModal}
          onClose={() => setShowConfirmModal(false)}
          onConfirm={handleConfirmSave}
          title="Teklif Kaydet ve Onayla"
          message="Bu teklif belgesini kaydetmek ve onaylamak istediğinizden emin misiniz? Onayladıktan sonra bu teklifi bir daha değiştiremezsiniz."
          confirmText="Evet, Kaydet ve Onayla"
          type="info"
          icon="fas fa-check-circle"
        />

        {/* Save Info Modal */}
        <ConfirmationModal
          isOpen={showSaveInfoModal}
          onClose={() => setShowSaveInfoModal(false)}
          onConfirm={async () => {
            setShowSaveInfoModal(false);
            await executeSaveOnly();
          }}
          title="Teklif Kaydet"
          message="Teklif bilgileriniz taslak olarak kaydedildi. Fiyatlandırmayı tamamladıktan sonra 'Kaydet ve Onayla' butonuna basmayı unutmayınız."
          confirmText="Kaydet"
          type="info"
          icon="fas fa-save"
        />

        {/* Item View Modal */}
        {selectedItemId && (
          <ItemViewPublic
            isOpen={showItemModal}
            onClose={() => {
              setShowItemModal(false);
              setSelectedItemId(null);
              setSelectedItemData(null);
            }}
            itemId={selectedItemId}
            itemData={selectedItemData}
            mode="view"
          />
        )}
      </main>
  );
};

export default QuoteDocumentPage;
